import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Select } from 'antd';

import { Colors } from 'context';
import { CareerIcon, CareerIconName } from 'atoms';
import { careerType, careerLayout } from 'services/careerType';

import { ISkillMapParams } from '../model/skillMapParams';
import { SkillHaveFilter } from '../model/skillGraphFilter';

/**
 * Панель управления картой (ТЗ 0070, переработка ТЗ 0071 п. 6.2).
 *
 * Каждый переключатель здесь меняет ТОЛЬКО адрес — панель не знает ни про
 * раскладку, ни про масштаб.
 *
 * 🔴 ТЗ 0071: в основной строке — только то, что трогают КАЖДУЮ минуту:
 * поиск, «Wszystkie / Brakuje / Już mam», «Mój następny krok» и «Cała mapa».
 * Группа, курс, «tylko kluczowe», зависимости и вид списком — это настройки,
 * их трогают раз за сессию, и они уехали в «Ustawienia widoku». Масштаб —
 * свойство холста, он переехал оверлеем в угол карты (`SkillMapZoomControls`).
 * Профессия осталась в крошках: корневая крошка И ЕСТЬ переключатель.
 *
 * 🔴 Значки — ЭХО ЛЕГЕНДЫ, а не украшение: «не хватает» помечено тем же знаком,
 * которым легенда помечает критический пробел, «уже есть» — галочкой.
 */
interface ISkillMapToolbar {
  params: ISkillMapParams;
  found: number | null;
  /** Группы текущей карты — для выбора раскрытой группы. */
  clusters: { code: string; title: string }[];
  /** Курсы, которые дают навыки этой карты. */
  courses: { id: string; name: string; n: number }[];
  /** Сколько узлов показано и сколько всего. */
  shown: number;
  total: number;
  filtersActive: boolean;
  /** Есть ли что рекомендовать: у закрытой профессии «следующего шага» нет. */
  hasNextStep: boolean;
  onChange: (patch: Partial<ISkillMapParams>) => void;
  onReset: () => void;
  onNextStep: () => void;
  onWholeMap: () => void;
}

const HAVE: { value: SkillHaveFilter | null; labelKey: string; icon?: CareerIconName }[] = [
  { value: null, labelKey: 'careerCenter.map.filterAll' },
  { value: 'gap', labelKey: 'careerCenter.map.filterGap', icon: 'warning' },
  { value: 'mine', labelKey: 'careerCenter.map.filterMine', icon: 'check' },
];

export const SkillMapToolbar = memo(
  ({
    params,
    found,
    clusters,
    courses,
    shown,
    total,
    filtersActive,
    hasNextStep,
    onChange,
    onReset,
    onNextStep,
    onWholeMap,
  }: ISkillMapToolbar) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    /**
     * Меню закрывается кликом мимо и Escape.
     *
     * 🔴 Своё раскрытие, а не antd `Dropdown`: внутри меню живут два antd
     * `Select`, чьи списки по умолчанию рисуются порталом у `<body>` — выбор
     * пункта считался бы «кликом мимо» и закрывал меню вместе со списком.
     * Списки направлены внутрь панели (`getPopupContainer`), и граница «внутри
     * / снаружи» совпадает с DOM. Заодно не зависим от анимаций antd, которые
     * в фоновой вкладке не доигрывают.
     */
    useEffect(() => {
      if (!open) return undefined;
      const onDown = (event: MouseEvent) => {
        if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false);
      };
      const onKey = (event: KeyboardEvent) => {
        if (event.key !== 'Escape' || event.defaultPrevented) return;
        // Escape закрывает МЕНЮ, а не паспорт под ним: одно нажатие — одно
        // закрытие, ближайшее к фокусу.
        event.preventDefault();
        setOpen(false);
      };
      document.addEventListener('mousedown', onDown);
      // Фаза ПЕРЕХВАТА: слушатель меню обязан успеть раньше паспорта, который
      // слушает Escape на всплытии, — иначе порядок решала бы очерёдность
      // подписки, то есть что человек открыл раньше.
      document.addEventListener('keydown', onKey, true);
      return () => {
        document.removeEventListener('mousedown', onDown);
        document.removeEventListener('keydown', onKey, true);
      };
    }, [open]);

    const popupContainer = useCallback(() => panelRef.current || document.body, []);

    // Триггер ОБЯЗАН показывать, что внутри что-то включено: иначе карта
    // выглядит поломанной, а причина спрятана в закрытом меню.
    const settingsOn =
      (params.focus ? 1 : 0) +
      (params.course ? 1 : 0) +
      (params.core ? 1 : 0) +
      (params.deps ? 1 : 0) +
      (params.view === 'tree' ? 1 : 0);

    return (
      <Root role="group" aria-label={t('careerCenter.map.toolbar')}>
        <Search
          type="search"
          value={params.q}
          placeholder={t('careerCenter.map.searchPlaceholder')}
          aria-label={t('careerCenter.map.searchPlaceholder')}
          onChange={(event) => onChange({ q: event.target.value })}
        />
        {found !== null && <Counter>{t('careerCenter.map.found', { n: found })}</Counter>}

        {/* Сегмент, а не три кнопки: у трёх значений один параметр, и общая
            рамка говорит об этом раньше, чем подписи. */}
        <Segment role="group" aria-label={t('careerCenter.map.filterStatus')}>
          {HAVE.map((row) => (
            <SegmentItem
              key={row.labelKey}
              type="button"
              $active={params.have === row.value}
              aria-pressed={params.have === row.value}
              onClick={() => onChange({ have: row.value })}>
              {row.icon && <CareerIcon name={row.icon} size={14} />}
              {t(row.labelKey)}
            </SegmentItem>
          ))}
        </Segment>

        {/* Счётчик и сброс стоят рядом намеренно: число объясняет, почему
            кнопка появилась, а кнопка — что с этим числом делать. */}
        {filtersActive && (
          <>
            <Counter>{t('careerCenter.map.shownOfShort', { shown, total })}</Counter>
            <Toggle
              type="button"
              aria-label={t('careerCenter.map.reset')}
              title={t('careerCenter.map.reset')}
              onClick={onReset}>
              ×
            </Toggle>
          </>
        )}

        {/* 🔴 Кнопка не прячется, когда шага нет: исчезающий орган управления
            читается как поломка. Она гаснет и объясняет почему. */}
        <Toggle
          type="button"
          disabled={!hasNextStep}
          title={hasNextStep ? undefined : t('careerCenter.map.nextStepNone')}
          onClick={onNextStep}>
          <CareerIcon name="goal" size={14} />
          {t('careerCenter.map.nextStep')}
        </Toggle>

        <Toggle
          type="button"
          $active={params.depth === 1 && !params.focus}
          aria-pressed={params.depth === 1 && !params.focus}
          onClick={onWholeMap}>
          {t('careerCenter.map.wholeMap')}
        </Toggle>

        <Settings ref={wrapRef}>
          <Toggle
            type="button"
            $active={settingsOn > 0}
            aria-expanded={open}
            aria-controls="skm-view-settings"
            onClick={() => setOpen((value) => !value)}>
            {t('careerCenter.map.viewSettings')}
            {settingsOn > 0 ? ` · ${settingsOn}` : ''}
          </Toggle>
          {open && (
            <Panel
              id="skm-view-settings"
              ref={panelRef}
              role="group"
              aria-label={t('careerCenter.map.viewSettings')}>
              <Field>
                <FieldLabel>{t('careerCenter.map.filterGroup')}</FieldLabel>
                <DenseSelect
                  // Список шире контрола: названия групп длиннее, и обрезать
                  // их в САМОМ списке значит прятать то, ради чего его открыли.
                  dropdownMatchSelectWidth={false}
                  getPopupContainer={popupContainer}
                  value={params.focus || undefined}
                  onChange={(value) => onChange({ focus: (value as string) || null, depth: 2 })}
                  allowClear
                  clearIcon={
                    <CareerIcon name="close" size={14} title={t('careerCenter.clearField')} />
                  }
                  onClear={() => onChange({ focus: null })}
                  placeholder={t('careerCenter.map.filterGroupAll')}
                  aria-label={t('careerCenter.map.filterGroup')}
                  options={clusters.map((cluster) => ({
                    value: cluster.code,
                    label: cluster.title || t('careerCenter.map.otherCluster'),
                  }))}
                />
              </Field>

              {courses.length > 0 && (
                <Field>
                  <FieldLabel>{t('careerCenter.map.filterCourse')}</FieldLabel>
                  <DenseSelect
                    dropdownMatchSelectWidth={false}
                    getPopupContainer={popupContainer}
                    value={params.course || undefined}
                    onChange={(value) => onChange({ course: (value as string) || null })}
                    allowClear
                    clearIcon={
                      <CareerIcon name="close" size={14} title={t('careerCenter.clearField')} />
                    }
                    onClear={() => onChange({ course: null })}
                    placeholder={t('careerCenter.map.filterCourseAll')}
                    aria-label={t('careerCenter.map.filterCourse')}
                    showSearch
                    optionFilterProp="label"
                    options={courses.map((course) => ({
                      value: course.id,
                      label: t('careerCenter.map.filterCourseOption', {
                        name: course.name,
                        n: course.n,
                      }),
                    }))}
                  />
                </Field>
              )}

              <Check
                type="button"
                role="switch"
                aria-checked={params.core}
                onClick={() => onChange({ core: !params.core })}>
                <Box $on={params.core} aria-hidden>
                  {params.core && <CareerIcon name="check" size={12} />}
                </Box>
                {t('careerCenter.map.onlyCore')}
              </Check>
              <Check
                type="button"
                role="switch"
                aria-checked={params.deps}
                disabled={params.depth === 1 && !params.focus}
                onClick={() => onChange({ deps: !params.deps })}>
                <Box $on={params.deps} aria-hidden>
                  {params.deps && <CareerIcon name="check" size={12} />}
                </Box>
                {t('careerCenter.map.dependencies')}
              </Check>
              <Check
                type="button"
                role="switch"
                aria-checked={params.view === 'tree'}
                onClick={() => onChange({ view: params.view === 'tree' ? 'canvas' : 'tree' })}>
                <Box $on={params.view === 'tree'} aria-hidden>
                  {params.view === 'tree' && <CareerIcon name="check" size={12} />}
                </Box>
                {t('careerCenter.map.listView')}
              </Check>
            </Panel>
          )}
        </Settings>
      </Root>
    );
  },
);

/**
 * 🔴 `box-sizing` задаётся ЗДЕСЬ, а не глобально. В проекте нет общего сброса —
 * это его свойство, а не упущение: два десятка компонентов посчитаны в
 * content-box и ставят себе `border-box` сами. Класс styled-components
 * уникален, за пределы панели правило не течёт.
 */
const Root = styled.div`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${careerLayout.denseGap}px;

  /* 0073: ряд переносится при нехватке места на ЛЮБОЙ ширине — контролы 44
     с текстом 14 не ужимаются и не обрезаются. */
`;

const Search = styled.input`
  ${careerType.meta()};
  height: ${careerLayout.hitTarget}px;
  padding: 0 8px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 8px;
  width: 200px;
  max-width: 100%;
  flex: 0 1 auto;
  color: ${Colors.black};
  background: ${Colors.white};
`;

const Counter = styled.span`
  ${careerType.meta()};
  white-space: nowrap;
  color: ${Colors.gray_dark};
`;

/**
 * 🔴 Селект antd, а не нативный. Нативный `<select>` в тёмной теме починить
 * НЕВОЗМОЖНО: его список рисует операционная система, и явные цвета красят
 * только закрытый контрол.
 */
const DenseSelect = styled(Select)`
  width: 100%;

  &&& .ant-select-selector {
    height: ${careerLayout.hitTarget}px;
    padding: 0 8px;
    border-radius: 8px;
  }

  &&& .ant-select-selection-item,
  &&& .ant-select-selection-placeholder {
    line-height: ${careerLayout.hitTarget - 2}px;
    font-size: 14px;
  }

  &&& .ant-select-selection-search-input {
    height: ${careerLayout.hitTarget - 2}px;
  }
` as typeof Select;

const Segment = styled.div`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  border: 1px solid ${Colors.system_gray};
  border-radius: 8px;

  & > button + button {
    border-left: 1px solid ${Colors.system_gray};
  }
`;

/**
 * 🔴 Активное состояние — подложка и жирное начертание, НЕ жёлтая заливка:
 * контраст #FFDF17 на белом примерно 1.4 : 1 при норме 4.5 : 1.
 */
const SegmentItem = styled.button<{ $active?: boolean }>`
  ${({ $active }) => ($active ? careerType.metaStrong() : careerType.meta())};
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: ${careerLayout.hitTarget}px;
  padding: 0 8px;
  border: none;
  background: ${({ $active }) => ($active ? Colors.bg_gray : 'transparent')};
  color: ${Colors.black};
  cursor: pointer;
  white-space: nowrap;

  &:first-child {
    border-radius: 7px 0 0 7px;
  }

  &:last-child {
    border-radius: 0 7px 7px 0;
  }
`;

const Toggle = styled.button<{ $active?: boolean }>`
  ${({ $active }) => ($active ? careerType.metaStrong() : careerType.meta())};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex: 0 0 auto;
  height: ${careerLayout.hitTarget}px;
  min-width: ${careerLayout.hitTarget}px;
  padding: 0 8px;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  color: ${Colors.black};
  background: ${({ $active }) => ($active ? Colors.bg_gray : Colors.white)};
  border: 1px solid ${({ $active }) => ($active ? Colors.black : Colors.system_gray)};

  &:disabled {
    color: ${Colors.gray_dark};
    border-color: ${Colors.system_gray};
    cursor: not-allowed;
  }
`;

const Settings = styled.div`
  position: relative;
  flex: 0 0 auto;
`;

/**
 * Меню настроек. Прижато к ПРАВОМУ краю кнопки: кнопка стоит последней в ряду,
 * и меню, раскрытое вправо, уезжало бы за край окна.
 */
const Panel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 280px;
  max-width: calc(100vw - 32px);
  padding: 12px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 12px;
  background: ${Colors.white};
  box-shadow: 0 8px 24px rgba(7, 7, 7, 0.12);

  /* 🔴 Ниже 1220 ряд переносится, и кнопка может встать у ЛЕВОГО края:
     меню, прижатое к её правому краю, уехало бы за левый край окна, а
     прижатое к левому — за правый, давая странице горизонтальную прокрутку.
     Поэтому на узких экранах меню во всю ширину окна с полями 16 px. */
  @media (max-width: 1219px) {
    position: fixed;
    top: auto;
    margin-top: 6px;
    left: 16px;
    right: 16px;
    width: auto;
    max-width: none;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.span`
  ${careerType.metaStrong()};
  color: ${Colors.black};
`;

const Check = styled.button`
  ${careerType.meta()};
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: ${careerLayout.hitTarget}px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${Colors.black};
  text-align: left;
  cursor: pointer;

  &:disabled {
    color: ${Colors.gray_dark};
    cursor: not-allowed;
  }
`;

const Box = styled.span<{ $on: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid ${({ $on }) => ($on ? Colors.black : Colors.gray_dark)};
  background: ${({ $on }) => ($on ? Colors.bg_gray : Colors.white)};
  color: ${Colors.black};
`;
